import {
  Carousel,
  CarouselControls,
  CarouselNavigation,
  CarouselPagination,
  CarouselSlide,
} from '@cinch-labs/design-system'
import React, {
  isValidElement,
  ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import Swiper from 'swiper'

import styles from './looping-carousel.module.css'

export interface SwiperContainer extends HTMLElement {
  swiper?: Swiper
}

interface LoopingCarouselProps {
  children: ReactNode
  slideSpeedInSeconds?: number
  shouldLoop?: boolean
}

const useAutoSlide = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  {
    shouldLoop,
    slideSpeedInSeconds,
    isPaused,
  }: {
    shouldLoop: boolean
    slideSpeedInSeconds: number
    isPaused: boolean
  },
) => {
  useEffect(() => {
    if (!shouldLoop || isPaused) return

    const interval = setInterval(() => {
      const swiperContainer = containerRef.current?.querySelector(
        '.swiper',
      ) as SwiperContainer
      swiperContainer?.swiper?.slideNext()
    }, slideSpeedInSeconds * 1000)

    return () => clearInterval(interval)
  }, [shouldLoop, slideSpeedInSeconds, isPaused, containerRef])
}

export default function LoopingCarousel({
  children,
  slideSpeedInSeconds = 2,
  shouldLoop = true,
}: LoopingCarouselProps) {
  const reactId = useId()
  const carouselId = useMemo(
    () => `carousel-${reactId.replace(/:/g, '')}`,
    [reactId],
  )
  const containerRef = useRef<HTMLDivElement>(null)
  const [isPaused, setIsPaused] = useState(false)

  useAutoSlide(containerRef, {
    shouldLoop,
    slideSpeedInSeconds,
    isPaused,
  })

  if (!children || React.Children.count(children) === 0) {
    return null
  }

  return (
    <div
      ref={containerRef}
      className={styles.carouselContent}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setIsPaused(false)
        }
      }}
    >
      <Carousel
        name={carouselId}
        customClassNames={{ carousel: styles.carousel }}
        swiperSettings={{
          slidesPerView: 1,
          spaceBetween: 24,
          loop: shouldLoop,
        }}
      >
        {React.Children.map(children, (child, index) => (
          <CarouselSlide
            key={isValidElement(child) && child.key != null ? child.key : index}
            className={styles.carouselSlide}
          >
            {child}
          </CarouselSlide>
        ))}
      </Carousel>

      <CarouselControls customClassNames={styles.controlsWrapper}>
        <CarouselPagination
          name={carouselId}
          customClassNames={styles.customPagination}
        />
        <CarouselNavigation
          name={carouselId}
          ariaLabels={{
            previous: 'Go to previous slide',
            next: 'Go to next slide',
          }}
        />
      </CarouselControls>
    </div>
  )
}
